import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { FormActions } from "../../components/ui/form";
import HeadOrder from "./headOrder";
import DetailOrder from "./detailOrder";
import Totals from "./totals";
import { ErrorModal } from "../../components/ModalAlert";
import supabase from "../../supabase";
import { LoaderSpinner } from "../../components/spinner/loader";
import { orderObjectStatus } from "../../constants/orderStatus";

const OrderDetailsForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state;

  const [order, setOrder] = useState({
    currencyRate: "",
  });
  const [orderDetails, setOrderDetails] = useState([]);
  const [deletePrevColumnsId, setDeletePrevColumnsId] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalExpensesWithIva, setTotalExpensesWithIva] = useState(0);
  const [totalExpensesWithIva21, setTotalExpensesWithIva21] = useState(0);
  const [totalExpensesWithIva105, setTotalExpensesWithIva105] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingOrderData, setLoadingOrderData] = useState(true);
  const [disableEdition, setDisableEdition] = useState(false);

  useEffect(() => {
    const loadOrderData = async () => {
      if (!data?.order?.orderId) {
        setLoadingOrderData(false);
        return;
      }

      setLoadingOrderData(true);

      try {
        const orderFromState = data.order;
        setOrder(orderFromState);
        setDisableEdition(orderFromState.status === orderObjectStatus.facturada);

        let details = orderFromState.orderDetails || orderFromState.details;
        if (!details?.length) {
          const { data: orderDetailsData, error: detailsError } = await supabase
            .from("orderDetails")
            .select(
              "orderDetailsId, orderId, description, price, quantity, ivaPercentage, currency, createdAt"
            )
            .eq("orderId", orderFromState.orderId);

          if (detailsError) {
            console.error("Error cargando detalles de la orden:", detailsError);
            details = [];
          } else {
            details = orderDetailsData || [];
          }
        }

        setOrderDetails(details);

        // Cargar archivos de la orden
        if (orderFromState.orderId && orderFromState.bucketId) {
          try {
            const orderId = orderFromState.orderId;
            const bucketId = orderFromState.bucketId;
            
            const { data: filesData, error } = await supabase.storage
              .from(bucketId)
              .list(`${orderId}/`);
            
            if (!error && filesData) {
              const files = await Promise.all(
                filesData.map(async (file) => {
                  const { data } = supabase.storage
                    .from(bucketId)
                    .getPublicUrl(`${orderId}/${file.name}`);
                  return data.publicUrl;
                })
              );
              
              setOrder(prevOrder => ({
                ...prevOrder,
                files: files
              }));
            }
          } catch (error) {
            console.error("Error cargando archivos:", error);
          }
        }
        
        // Cargar datos de firma si no están presentes
        if (
          orderFromState.orderId &&
          (!orderFromState.clientSignaturePhoto || !orderFromState.clientSignatureName)
        ) {
          try {
            const { data: orderFromDb, error: orderDbError } = await supabase
              .from("orders")
              .select("orderId, clientSignaturePhoto, clientSignatureName")
              .eq("orderId", orderFromState.orderId)
              .single();
              
            if (!orderDbError && orderFromDb) {
              setOrder(prevOrder => ({
                ...prevOrder,
                clientSignaturePhoto: orderFromDb.clientSignaturePhoto,
                clientSignatureName: orderFromDb.clientSignatureName
              }));
            }
          } catch (error) {
            console.error("Error cargando datos de firma:", error);
          }
        }
      } finally {
        setLoadingOrderData(false);
      }
    };

    loadOrderData();

    // Función de limpieza
    return () => {
      setOrder({});
      setOrderDetails([]);
      setDeletePrevColumnsId([]);
      setTotalExpenses(0);
      setTotalExpensesWithIva(0);
      setTotalExpensesWithIva21(0);
      setTotalExpensesWithIva105(0);
    };
  }, [data]);

  const saveOperation = async () => {
    try {
      // Verificar si hay detalles con moneda USD
      const hasUSDDetails = orderDetails.some(
        detail => detail.currency !== 1 && detail.currency !== "1"
      );

      // Validar cotización si hay detalles en USD
      if (hasUSDDetails) {
        const rate = parseFloat(order?.currencyRate);
        if (!order?.currencyRate || order.currencyRate === "" || isNaN(rate) || rate <= 0) {
          ErrorModal({
            title: "ATENCIÓN",
            text: "Debe ingresar un valor de cotización válido!",
          });
          return;
        }
      }

      setLoading(true);

      // Actualizar orden principal
      const { error: errorOrder } = await supabase
        .from("orders")
        .update({
          status: order.status,
          currencyRate:
            typeof order.currencyRate === "string"
              ? Number(order.currencyRate.replace(",", "."))
              : order.currencyRate,
          iva210: totalExpensesWithIva21,
          iva105: totalExpensesWithIva105,
          subTotal: totalExpenses,
        })
        .eq("orderId", order.orderId);

      if (errorOrder) throw new Error(errorOrder.message);

      // Eliminar detalles marcados para eliminación
      for (const deletedItem of deletePrevColumnsId) {
        const { error } = await supabase
          .from("orderDetails")
          .delete()
          .eq("orderDetailsId", deletedItem);

        if (error)
          throw new Error(`Error eliminando detalle: ${error.message}`);
      }

      // Actualizar o insertar detalles
      for (const det of orderDetails) {
        if (det.orderDetailsId) {
          // Actualizar detalle existente
          const { error: errorDetails } = await supabase
            .from("orderDetails")
            .update({
              quantity: Number(det.quantity),
              description: det.description,
              price: Number(det.price),
              currency: det.currency,
              ivaPercentage: det.ivaPercentage,
            })
            .eq("orderDetailsId", det.orderDetailsId);

          if (errorDetails)
            throw new Error(
              `Error actualizando detalle: ${errorDetails.message}`
            );
        } else {
          // Insertar nuevo detalle
          const { error: errorOrderDetails } = await supabase
            .from("orderDetails")
            .insert([
              {
                orderId: order.orderId,
                quantity: Number(det.quantity),
                description: det.description,
                price: Number(det.price),
                currency: det.currency,
                ivaPercentage: det.ivaPercentage,
              },
            ]);

          if (errorOrderDetails)
            throw new Error(
              `Error insertando detalle: ${errorOrderDetails.message}`
            );
        }
      }

      navigate(-1);
    } catch (error) {
      ErrorModal({
        title: "Error al guardar",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return loading || loadingOrderData ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", minWidth: 0 }}>
      <HeadOrder
        order={order}
        setOrder={setOrder}
        disableEdition={disableEdition}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
      />
      <DetailOrder
        order={order}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        setTotalExpenses={setTotalExpenses}
        setTotalExpensesWithIva={setTotalExpensesWithIva}
        setTotalExpensesWithIva21={setTotalExpensesWithIva21}
        setTotalExpensesWithIva105={setTotalExpensesWithIva105}
        disableEdition={disableEdition}
        setDeletePrevColumnsId={setDeletePrevColumnsId}
      />
      <Totals
        totalExpenses={totalExpenses}
        totalExpensesWithIva={totalExpensesWithIva}
        iva21={totalExpensesWithIva21}
        iva105={totalExpensesWithIva105}
      />
      <FormActions>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ minHeight: 44 }}>
          {disableEdition ? "Volver" : "Cancelar"}
        </Button>
        {!disableEdition && (
          <Button
            variant="contained"
            color="primary"
            onClick={saveOperation}
            sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 200 }}
          >
            Guardar cambios
          </Button>
        )}
      </FormActions>
    </Box>
  );
};

export default OrderDetailsForm;
